import { useEffect, useState } from 'react';
import { z } from 'zod';
import { getPasswordPolicy, PasswordPolicy } from '../api/Auth';
import { logger } from '../utils/logger';

// Если политику получить не удалось, проверяем хотя бы длину — так же,
// как сервер по умолчанию. Окончательно пароль всё равно проверит сервер.
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
    minLength: 8,
    maxLength: null,
    minUpperCase: 0,
    minLowerCase: 0,
    minDigits: 0,
    minSpecialChars: 0,
    notUsername: false
};

// Политика realm'а меняется редко: один запрос на всё время жизни вкладки.
let policyRequest: Promise<PasswordPolicy> | null = null;

function loadPasswordPolicy(): Promise<PasswordPolicy> {
    if (!policyRequest) {
        policyRequest = getPasswordPolicy().catch((error) => {
            logger.debug('password policy request failed', error);
            // Не кэшируем неудачу: следующая форма попробует снова.
            policyRequest = null;
            return DEFAULT_PASSWORD_POLICY;
        });
    }

    return policyRequest;
}

export function usePasswordPolicy(): PasswordPolicy {
    const [policy, setPolicy] = useState<PasswordPolicy>(DEFAULT_PASSWORD_POLICY);

    useEffect(() => {
        let isActive = true;

        loadPasswordPolicy().then((loaded) => {
            if (isActive) {
                setPolicy(loaded);
            }
        });

        return () => {
            isActive = false;
        };
    }, []);

    return policy;
}

// Классы символов считаем так же, как сервер (char.IsUpper / IsLower /
// IsDigit / !IsLetterOrDigit), иначе подсказка и ответ сервера разойдутся.
const isDigit = (c: string): boolean => c >= '0' && c <= '9';
const isLetter = (c: string): boolean => c.toLowerCase() !== c.toUpperCase();
const isUpper = (c: string): boolean => isLetter(c) && c === c.toUpperCase();
const isLower = (c: string): boolean => isLetter(c) && c === c.toLowerCase();
const isSpecial = (c: string): boolean => !isLetter(c) && !isDigit(c);

function count(password: string, predicate: (c: string) => boolean): number {
    return Array.from(password).filter(predicate).length;
}

/** Одно правило политики: ключ перевода в namespace password, его параметры и проверка. */
export interface PasswordRequirement {
    key: string;
    params: Record<string, number>;
    test: (password: string) => boolean;
}

export function buildPasswordRequirements(policy: PasswordPolicy, username?: string): PasswordRequirement[] {
    const requirements: PasswordRequirement[] = [
        { key: 'rule_min_length', params: { count: policy.minLength }, test: (p) => p.length >= policy.minLength }
    ];

    const { maxLength } = policy;
    if (maxLength !== null) {
        requirements.push({ key: 'rule_max_length', params: { count: maxLength }, test: (p) => p.length <= maxLength });
    }
    if (policy.minUpperCase > 0) {
        requirements.push({ key: 'rule_upper', params: { count: policy.minUpperCase }, test: (p) => count(p, isUpper) >= policy.minUpperCase });
    }
    if (policy.minLowerCase > 0) {
        requirements.push({ key: 'rule_lower', params: { count: policy.minLowerCase }, test: (p) => count(p, isLower) >= policy.minLowerCase });
    }
    if (policy.minDigits > 0) {
        requirements.push({ key: 'rule_digits', params: { count: policy.minDigits }, test: (p) => count(p, isDigit) >= policy.minDigits });
    }
    if (policy.minSpecialChars > 0) {
        requirements.push({ key: 'rule_special', params: { count: policy.minSpecialChars }, test: (p) => count(p, isSpecial) >= policy.minSpecialChars });
    }
    if (policy.notUsername && username) {
        const lowered = username.toLowerCase();
        requirements.push({ key: 'rule_not_username', params: {}, test: (p) => !p.toLowerCase().includes(lowered) });
    }

    return requirements;
}

/** Zod-схема нового пароля: в issue.message — ключ перевода невыполненного правила. */
export function buildPasswordSchema(requirements: PasswordRequirement[]): z.ZodType<string> {
    return z.string().superRefine((password, ctx) => {
        for (const requirement of requirements) {
            if (!requirement.test(password)) {
                ctx.addIssue({ code: 'custom', message: requirement.key, params: requirement.params });
            }
        }
    });
}
